/**
 * Station-volumes exports: the CSV a QS downloads from the dock and the TSV
 * they paste into a spreadsheet.
 *
 * The CSV's `#` provenance block and its 17 columns are **the worker's**
 * (`scripts/station_bins.py :: station_csv`, spec §4f), not a second dialect:
 * the in-app download and the job's byproduct file carry the same name, so
 * they have to be the same document. `stationExport.test.ts` reads the Python
 * source and cross-checks both halves mechanically (ruling T12.1).
 *
 * Numbers are raw decimals with a `.` separator and no thousands separator,
 * and an ASCII hyphen for negatives — Excel, pandas and QGIS all parse that
 * as a number. Only pandas (`comment='#'`) and QGIS's delimited-text reader
 * skip the `#` block; Excel imports those lines as rows for the reader to
 * delete, which is why the block is short and reads as prose.
 */
import { internalToDisplayRaw } from '@/core/alignment';

import { staOnly } from './format';
import type { StationBin, StationCrossfall, StationSectionLine, StationVolumesJson } from './types';

export interface StationExportOptions {
  /** Inclusive bin range; omit both for every bin. */
  fromBin?: number;
  toBin?: number;
  /** Defaults to now. Injected so the header is reproducible in tests. */
  generatedAtISO?: string;
  /** The two compared surfaces, for the `# Analysis:` line. */
  topLayerName?: string;
  bottomLayerName?: string;
  /** Which analysis produced the bins; defaults to `'surface difference'` so
   *  an existing surface-difference export stays byte-identical. Mirrors
   *  `result["analysisKind"]` in `scripts/station_bins.py::station_csv`. */
  analysisKind?: string;
  /** The live alignment no longer hashes to the one the run used
   *  (`alignmentHashMatches`). Adds a warning line to the `#` block — see
   *  `stationCsvHeaderLines`. */
  alignmentStale?: boolean;
}

/** The worker's `_CSV_COLUMNS`, in the worker's order. */
export const STATION_CSV_COLUMNS = [
  'sta_start_display',
  'sta_end_display',
  'sta_start_internal',
  'sta_end_internal',
  'length_m',
  'cut_m3',
  'fill_m3',
  'net_m3',
  'cum_net_m3',
  'cut_area_m2',
  'fill_area_m2',
  'cut_aea_m3',
  'fill_aea_m3',
  'cell_count',
  'coverage_pct',
  'low_coverage',
  'z_top_mean_m',
] as const;

/**
 * The worker's `_EXPECTATION_CSV_COLUMNS`, in the worker's order — APPENDED
 * to `STATION_CSV_COLUMNS`, never inserted (ruling T12.1). Present only when
 * `json.expectation` is set (a run that carried an expectation); a `NONE`
 * run's CSV keeps exactly the 17 base columns, byte for byte, which is what
 * the existing fixture pins.
 *
 * `crossing_pct` is deliberately NOT here: nine columns are the contract.
 * The consequence — under/in-spec/over area % do not sum to 100 where a cell
 * crosses the target — is stated in the `# Expectation:` header line instead
 * (`stationCsvHeaderLines`).
 */
export const STATION_EXPECTATION_CSV_COLUMNS = [
  'mean_thickness_m',
  'design_thickness_m',
  'deviation_m',
  'under_area_pct',
  'in_spec_area_pct',
  'over_area_pct',
  'not_measured_pct',
  'deficit_volume_m3',
  'verdict',
] as const;

/**
 * `f"{v:.{d}f}"`, not `v.toFixed(d)`.
 *
 * The two agree on every value except an exact tie at the rounding digit,
 * where Python rounds half to **even** and `toFixed` rounds half **away from
 * zero**: `f"{62.25:.1f}"` is `62.2`, `(62.25).toFixed(1)` is `62.3`. Ties
 * are reachable here — `coverage_pct` is `100 · cells / binCells`, and
 * 249/400 lands exactly on 62.25 — and a single differing digit is enough to
 * make two files that claim to be the same file diff.
 *
 * `toFixed(100)` — the maximum, and a full exact expansion for anything in
 * this range — gives us the value's own digits to round ourselves. 20 digits
 * was not enough: `(5e-5).toFixed(20)` is `0.00005000000000000000`, which
 * *looks* like an exact half and would round to even (`0.0000`), while the
 * stored double is `0.000050000000000000002…` and Python prints `0.0001`.
 *
 * Never `-0` (fix round 1, Task 16): the worker's `_csv_num` rounds first,
 * THEN checks whether the ROUNDED value is zero, and prints a bare `0` if
 * so — never `-0`, whether the input was an exact `-0.0` or merely a tiny
 * negative that rounds away to zero at this precision (`-0.00001` at `d=4`).
 * Every existing base column reaches this function with that already
 * guaranteed upstream (`station_bins.py`'s `_pz` normalises every base
 * figure before it is even written to JSON), so the gap was invisible until
 * the nine expectation columns: `deviation_m`/`design_thickness_m` are
 * differences of two independently-computed means (`expectation.py`'s
 * `thickness_stats`) that are NOT run through `_pz`, and in MATCH mode
 * routinely land on a tiny negative. Matching `_csv_num` exactly (round,
 * then test the rounded value, not the input) is what keeps this correct
 * for the base columns too — not just the new ones.
 */
export function pyFixed(v: number, d: number): string {
  if (v == null || !Number.isFinite(v)) return '';
  // toFixed's exact-expansion trick only holds below 1e21; above it toFixed
  // switches to exponential notation. No corridor volume goes there, but a
  // wrong number is worse than a coarse one.
  if (Math.abs(v) >= 1e21) return v.toFixed(d);

  const negative = v < 0 || Object.is(v, -0);
  const exact = Math.abs(v).toFixed(100);
  const dot = exact.indexOf('.');
  const digits = exact.slice(0, dot) + exact.slice(dot + 1);
  let intLen = dot;

  const cut = intLen + d;
  let keep = digits.slice(0, cut);
  const rest = digits.slice(cut);

  const first = rest.charCodeAt(0) - 48;
  let roundUp = first > 5;
  if (first === 5) {
    roundUp = /[1-9]/.test(rest.slice(1)) ? true : (keep.charCodeAt(cut - 1) - 48) % 2 === 1; // exact half → to even
  }

  if (roundUp) {
    const out = keep.split('');
    let i = out.length - 1;
    for (; i >= 0; i--) {
      if (out[i] === '9') {
        out[i] = '0';
      } else {
        out[i] = String(Number(out[i]) + 1);
        break;
      }
    }
    keep = out.join('');
    if (i < 0) {
      keep = `1${keep}`;
      intLen += 1;
    }
  }

  // `keep` holds every kept digit (integer part + fraction) POST-rounding --
  // "all zero digits" is the rounded-value-is-zero test, done on the exact
  // digits the string below is built from, matching `round(v, d) == 0` in
  // `_csv_num`.
  const roundsToZero = /^0*$/.test(keep);

  const whole = keep.slice(0, intLen);
  const frac = keep.slice(intLen);
  return `${negative && !roundsToZero ? '-' : ''}${whole}${d > 0 ? `.${frac}` : ''}`;
}

/**
 * The worker's `_one_line`: collapse CR/LF runs in a name to one space.
 *
 * Every name in the `#` provenance block (alignment, the two surfaces, the
 * alignment's layer) is user-typed and can legally contain a newline. Left
 * alone it splits one comment line into two, and the second half — no longer
 * starting with `#` — is read as a data row by pandas, QGIS and Excel alike.
 */
const oneLine = (s: string | null | undefined): string | undefined =>
  s == null ? undefined : s.replace(/[\r\n]+/g, ' ');

/** The worker's `n()`: a fixed-point figure, or empty for a missing one. */
const n = (v: number | null | undefined, d = 4): string => (v == null || !Number.isFinite(v) ? '' : pyFixed(v, d));

const binsInRange = (json: StationVolumesJson, o: StationExportOptions): StationBin[] => {
  if (o.fromBin == null && o.toBin == null) return json.bins;
  const last = json.bins.length - 1;
  const lo = Math.max(0, Math.min(o.fromBin ?? 0, o.toBin ?? last));
  const hi = Math.min(last, Math.max(o.fromBin ?? 0, o.toBin ?? last));
  return json.bins.slice(lo, hi + 1);
};

/**
 * The nine expectation columns' source, none of them invented at the writing
 * site (mirrors `station_bins.py::station_csv`'s own comment):
 *
 * | column               | source                                     |
 * |----------------------|---------------------------------------------|
 * | mean_thickness_m     | `b.meanThicknessM`                           |
 * | design_thickness_m   | `b.meanThicknessM - b.meanDeviationM`        |
 * | deviation_m          | `b.meanDeviationM`                           |
 * | under_area_pct       | `b.underPct`                                 |
 * | in_spec_area_pct     | `b.inSpecPct`                                |
 * | over_area_pct        | `b.overPct`                                  |
 * | not_measured_pct     | `100 - b.coveragePct` (pre-existing key)     |
 * | deficit_volume_m3    | `b.deficitVolumeM3`                          |
 * | verdict              | `b.verdict`                                  |
 *
 * `design_thickness_m` is NOT the corridor-wide `json.expectation.thicknessM`
 * scalar — that is only right for MATCH/CONSTANT, while SURFACES' design
 * varies bin to bin. The subtraction is exact and correct in all three modes
 * (`meanDeviationM = mean(dz) - mean(expected)`, so `meanThicknessM -
 * meanDeviationM == mean(expected)`).
 *
 * Null handling: `meanThicknessM`/`meanDeviationM` (and so
 * `design_thickness_m`, which needs both) are `null` for a bin that measured
 * nothing — rendered as an EMPTY field via `n()`, never `0`, which would
 * claim a real measurement. The other six figures are never `null`/
 * `undefined` on a run that has an expectation at all.
 */
/**
 * The worker's `b["key"]` dict subscript, which raises `KeyError` when the
 * KEY itself is absent (a malformed/mismatched bin — `exp_bin` failed to run
 * or a caller merged the wrong dict) but returns `None` cleanly when the key
 * is present with no value (a bin that measured nothing). `b.key` in
 * TypeScript conflates those two cases into one `undefined` — this
 * distinguishes them the way the worker's subscript does, on real
 * JSON-sourced data where `undefined` only ever means "key absent" (JSON has
 * no `undefined` literal).
 *
 * Fix round 1, Task 16 finding 2: this used to be silent — `n(b.deficitVolumeM3)`
 * and `b.verdict ?? ''` both quietly rendered a blank cell for a missing key,
 * while the worker's `b["deficitVolumeM3"]` / `b["verdict"]` throw. A missing
 * expectation key on a run that has `json.expectation` set means the upstream
 * block itself is malformed; a blank cell hides exactly that, so both sides
 * now fail the same way — loudly — instead of one of them papering over it.
 */
const requireExpectationField = <K extends keyof StationBin>(
  b: StationBin,
  key: K,
): Exclude<StationBin[K], undefined> => {
  const v = b[key];
  // `undefined` on real JSON-sourced data only ever means "key absent"
  // (JSON has no `undefined` literal) — `null` (allowed for
  // meanThicknessM/meanDeviationM) is a real, present value and passes
  // through untouched.
  if (v === undefined) {
    throw new Error(`station bin ${b.i} carries an expectation but is missing "${key}"`);
  }
  return v as Exclude<StationBin[K], undefined>;
};

const expectationRow = (b: StationBin): string[] => {
  // `meanThicknessM`/`meanDeviationM` are the two keys allowed to be `null`
  // (a bin that measured nothing) — everything else below is never `null`
  // on a well-formed expectation bin, so a missing OR null value there is
  // equally a malformed upstream block.
  const meanThickness = requireExpectationField(b, 'meanThicknessM');
  const meanDeviation = requireExpectationField(b, 'meanDeviationM');
  const designThickness = meanThickness == null || meanDeviation == null ? null : meanThickness - meanDeviation;
  const underPct = requireExpectationField(b, 'underPct');
  const inSpecPct = requireExpectationField(b, 'inSpecPct');
  const overPct = requireExpectationField(b, 'overPct');
  const deficitVolumeM3 = requireExpectationField(b, 'deficitVolumeM3');
  const verdict = requireExpectationField(b, 'verdict');
  return [
    n(meanThickness),
    n(designThickness),
    n(meanDeviation),
    n(underPct, 1),
    n(inSpecPct, 1),
    n(overPct, 1),
    n(100.0 - b.coveragePct, 1),
    n(deficitVolumeM3),
    verdict,
  ];
};

const row = (b: StationBin, hasExpectation: boolean): string[] => [
  n(b.staStartDisplayM, 2),
  n(b.staEndDisplayM, 2),
  n(b.staStartInternalM, 2),
  n(b.staEndInternalM, 2),
  n(b.staEndInternalM - b.staStartInternalM, 2),
  n(b.cutVolumeM3),
  n(b.fillVolumeM3),
  n(b.netVolumeM3),
  n(b.cumulativeNetM3),
  n(b.cutAreaM2),
  n(b.fillAreaM2),
  n(b.cutVolumeAeaM3),
  n(b.fillVolumeAeaM3),
  String(b.cellCount),
  n(b.coveragePct, 1),
  b.lowCoverage ? '1' : '0',
  n(b.zTopMeanM, 3),
  ...(hasExpectation ? expectationRow(b) : []),
];

/**
 * The worker's `_station_tag`, digit for digit: `1+000.00`.
 *
 * This is deliberately not `staOnly` (ruling G10's single UI formatter):
 * `formatStation` rounds the minor part with `toFixed`, i.e. half away from
 * zero, and the CSV has to round half to even like the worker does. The
 * shape of the string is identical; only the last digit's tie-break differs,
 * and in this one file the worker's tie-break is the contract. A minor part
 * that rounds up to 1000.00 prints as `0+1000.00` here exactly as it does in
 * the worker — mirroring the quirk is the point.
 */
const csvStationTag = (display: number): string => {
  const sign = display < 0 ? '-' : '';
  const abs = Math.abs(display);
  const major = Math.floor(abs / 1000);
  return `${sign}${major}+${pyFixed(abs - major * 1000, 2).padStart(6, '0')}`;
};

/**
 * The `# Expectation:` line's "Design:" clause — the worker's
 * `_expectation_design_text`. `SURFACES` has no single number (the design
 * varies bin to bin, which `design_thickness_m` samples per row), so the
 * header names the shape of the check instead of inventing a corridor-wide
 * figure.
 *
 * No `null`-thickness fallback here (fix round 1, Task 16 finding 4): the
 * only mode left after the two returns above is CONSTANT, and
 * `json.expectation.thicknessM` is only ever `null` for SURFACES (the
 * worker's `exp_meta`, `compute_station_bins`). A branch that pretended to
 * handle a CONSTANT run with no thickness was unreachable and, worse,
 * looked like real behaviour to the next reader — deleted rather than kept
 * "just in case".
 *
 * Fix round 2: the deletion first landed as `pyFixed(exp.thicknessM as
 * number, 3)` — a cast, not a check. `pyFixed` returns `''` for `null`, so a
 * `CONSTANT` run that ever DID violate the invariant would have silently
 * produced a malformed `Design:  m constant` line here, while the worker's
 * `f"{exp['thicknessM']:.3f}"` raises `TypeError` on the exact same input —
 * the matched "both sides say n/a" pair the deletion removed became a
 * MISMATCHED pair (Python loud, TypeScript silent), the same asymmetry
 * class finding 2 had just fixed elsewhere in this file. The explicit throw
 * below is what actually gives this side the loud failure the comment above
 * describes — true of the worker's format spec and now, at last, true here
 * too.
 */
const expectationDesignText = (exp: NonNullable<StationVolumesJson['expectation']>): string => {
  if (exp.mode === 'SURFACES') return 'design surface (varies by station)';
  if (exp.mode === 'MATCH') return 'match design level';
  if (exp.thicknessM == null) {
    throw new Error(`CONSTANT expectation is missing thicknessM`);
  }
  return `${pyFixed(exp.thicknessM, 3)} m constant`;
};

/** The worker's `_expectation_tol_text`. */
const expectationTolText = (v: number | null): string => (v == null ? 'none' : `${pyFixed(v, 3)} m`);

/**
 * The worker's `_expectation_header_line`: names the mode, the design and
 * the tolerances (design doc §9.1), so a CSV with the nine appended
 * expectation columns is self-describing. Also states the one consequence
 * of capping the CSV at nine columns instead of ten (ruling T12.1):
 * `crossing_pct` is not a column, so under/in-spec/over area % do not sum to
 * 100 where a cell crosses the target.
 */
const expectationHeaderLine = (exp: NonNullable<StationVolumesJson['expectation']>): string =>
  `# Expectation: ${exp.mode}   Design: ${expectationDesignText(exp)}   ` +
  `Under tol: ${expectationTolText(exp.underToleranceM)}   ` +
  `Over tol: ${expectationTolText(exp.overToleranceM)}   ` +
  `(under/in-spec/over area % may not sum to 100 where a cell crosses the target)`;

/**
 * The `#` provenance block, line for line as `station_csv` writes it: em dash
 * in the title, U+2212 between the two surfaces, U+2013 in the internal
 * range. Exported so the dock can show the same provenance if it ever needs
 * to, and so the cross-check test has one thing to compare.
 */
export function stationCsvHeaderLines(
  json: StationVolumesJson,
  o: StationExportOptions = {},
  /** The first line's subject, matching the worker's `_csv_header_lines`
   *  `title` argument — "Station volumes" or "Station cross-sections". */
  title = 'Station volumes',
): string[] {
  const { alignment: a, params: p } = json;
  const top = oneLine(o.topLayerName) ?? '';
  const bottom = oneLine(o.bottomLayerName) ?? '';
  const kind = oneLine(o.analysisKind) || 'surface difference';
  const analysis = top || bottom ? `# Analysis: ${top} − ${bottom} (${kind})` : `# Analysis: (${kind})`;
  const display = (internal: number) => csvStationTag(internalToDisplayRaw(internal, a.staStart, a.equations));
  // The owning layer's NAME when the run resolved it, else its id — same
  // fallback, same order, as `station_bins.station_csv`.
  const layerLabel = oneLine(a.layerName) || oneLine(a.layerId) || '';

  const lines = [
    `# ${title} — ${oneLine(a.name) ?? ''}`,
    analysis,
    `# Alignment hash: ${a.sourceHash}   Layer: ${layerLabel}`,
    `# Interval: ${pyFixed(p.stationIntervalM, 2)} m   ` +
      `Swath: ${pyFixed(p.swathLeftM, 2)} L / ${pyFixed(p.swathRightM, 2)} R m`,
    `# Method: raster integration   ` +
      `Effective resolution: ${pyFixed(p.effectiveResolutionM, 3)} m   ` +
      `Cell area: ${pyFixed(p.cellAreaM2, 4)} m2`,
    `# Station range: ${display(p.stationFromInternalM)} to ${display(p.stationToInternalM)} ` +
      `(internal ${pyFixed(p.stationFromInternalM, 2)}–${pyFixed(p.stationToInternalM, 2)})`,
    `# Generated: ${o.generatedAtISO ?? new Date().toISOString()}`,
  ];

  // A range export states its own range. Without this line the header says
  // the run covered 1+000 to 1+500 while the rows stop at 1+240, and the
  // reader has no way to tell a partial export from a short run. The line is
  // ADDITIONAL — an all-bins export is still byte-identical to the worker's.
  const bins = binsInRange(json, o);
  if (bins.length > 0 && bins.length < json.bins.length) {
    const first = bins[0];
    const last = bins[bins.length - 1];
    // After the line it qualifies, found by its prefix rather than by a
    // hard-coded index — the worker owns the order of this block, and a line
    // added there must not silently move ours somewhere meaningless.
    const after = lines.findIndex((l) => l.startsWith('# Station range:'));
    lines.splice(
      after >= 0 ? after + 1 : lines.length,
      0,
      `# Selection: ${csvStationTag(first.staStartDisplayM)} to ${csvStationTag(last.staEndDisplayM)} ` +
        `(internal ${pyFixed(first.staStartInternalM, 2)}–${pyFixed(last.staEndInternalM, 2)}) ` +
        `— subset of the run above`,
    );
  }

  // Present only on a run that carried an expectation (Task 16), and the
  // worker's own `_csv_header_lines` appends it in exactly this position —
  // after `# Generated:` (and so after `# Selection:`, which is spliced in
  // above it), before the client-only `# Warning:` line below.
  if (json.expectation) lines.push(expectationHeaderLine(json.expectation));

  // The dock warns about a stale alignment in three places (the header chip,
  // the bands legend and the 3D overlay's border); the CSV leaves the building
  // with none of that context, and its `# Alignment hash:` line says nothing
  // without the live alignment to compare it against. LAST in the block, and
  // only when it applies: everything above is the worker's, in the worker's
  // order, so an up-to-date export is still byte-identical to the byproduct
  // file (T12.1).
  if (o.alignmentStale) lines.push('# Warning: alignment changed since this analysis');
  return lines;
}

/** The download: provenance block, column header, one line per bin. */
export function stationVolumesToCsv(json: StationVolumesJson, o: StationExportOptions = {}): string {
  const hasExpectation = json.expectation != null;
  const columns = hasExpectation ? [...STATION_CSV_COLUMNS, ...STATION_EXPECTATION_CSV_COLUMNS] : STATION_CSV_COLUMNS;
  const lines = [...stationCsvHeaderLines(json, o), columns.join(',')];
  for (const b of binsInRange(json, o)) lines.push(row(b, hasExpectation).join(','));
  return `${lines.join('\n')}\n`;
}

/**
 * The clipboard flavour: tab separated, no `#` block. A spreadsheet pastes
 * this straight into cells; the `#` lines would land in column A and have to
 * be deleted by hand every time.
 */
export function stationVolumesToTsv(json: StationVolumesJson, o: StationExportOptions = {}): string {
  const hasExpectation = json.expectation != null;
  const columns = hasExpectation ? [...STATION_CSV_COLUMNS, ...STATION_EXPECTATION_CSV_COLUMNS] : STATION_CSV_COLUMNS;
  const lines = [columns.join('\t')];
  for (const b of binsInRange(json, o)) lines.push(row(b, hasExpectation).join('\t'));
  return lines.join('\n');
}

/** The worker's `_SECTION_CSV_COLUMNS`, in the worker's order. */
export const STATION_SECTIONS_CSV_COLUMNS = [
  'sta_display',
  'sta_internal',
  'cut_area_m2',
  'fill_area_m2',
  'coverage_pct',
] as const;

/**
 * The worker's `_SECTION_CROSSFALL_CSV_COLUMNS`, in the worker's order.
 * APPENDED, never inserted, and only on a run that carried a check — the same
 * rule the interval CSV's expectation columns follow, and what keeps the two
 * shipped no-crossfall sections fixtures byte-identical.
 */
export const STATION_SECTIONS_CROSSFALL_CSV_COLUMNS = [
  'xf_left_asbuilt_pct',
  'xf_left_design_pct',
  'xf_left_delta_pct',
  'xf_left_verdict',
  'xf_right_asbuilt_pct',
  'xf_right_design_pct',
  'xf_right_delta_pct',
  'xf_right_verdict',
] as const;

/**
 * `station_bins.py::_section_crossfall_cells` — the eight RAW crossfall cells
 * of one carrier of a `crossfall` block. `n(null)` is the blank the worker's
 * `_csv_num` prints, so a side with no figure reads as "nothing was measured
 * here" in both languages — a `0.0000` would say "the road is flat", which is
 * a measurement. All three absences reach it: no `crossfall` at all, a side
 * the run never configured (`right: null`), and an INCONCLUSIVE side whose
 * figures are `null` while its verdict is real and kept.
 *
 * Takes the BLOCK, not a station line, so the stack's station CSV
 * (`layerStack/csv.ts`, whose carrier is a `StationBin`) shares this one
 * implementation — the Python has exactly one too, and reaches across modules
 * for it (`stack_analysis.py::stack_stations_csv`) rather than restating it.
 *
 * RAW, i.e. the verdicts are NOT quoted here: `station_sections_csv` appends
 * these cells as they come, while `stackStationsCsv` quotes the two
 * `*_verdict` ones itself. That difference between the two documents is
 * deliberate and lives at the call sites.
 */
export const crossfallCells = (carrier: { crossfall?: StationCrossfall }): string[] => {
  const out: string[] = [];
  for (const key of ['left', 'right'] as const) {
    const b = carrier.crossfall?.[key] ?? null;
    out.push(n(b?.asbuiltPct ?? null), n(b?.designPct ?? null), n(b?.deltaPct ?? null), b?.verdict ?? '');
  }
  return out;
};

/**
 * One row per station LINE: the TRUE end area where the section plane cuts,
 * which is the figure a cross-section sheet reports.
 *
 * A separate document from `station_volumes.csv`, exactly as it is server-side
 * (`station_bins.py::station_sections_csv`): that one is one row per INTERVAL
 * and these are one row per LINE, so they do not fit in one table, and its 17
 * columns are a byte-identity contract this must not disturb.
 *
 * Returns `null` when the run predates true sections — there is nothing to
 * write, and an empty CSV with a header would claim otherwise.
 */
export function stationSectionsToCsv(json: StationVolumesJson, o: StationExportOptions = {}): string | null {
  const lines = sectionLinesInRange(json, o);
  if (!lines) return null;
  // Gated on `params.crossfall`, exactly like the worker
  // (`station_bins.station_sections_csv`): the columns describe the RUN, not
  // the rows, so a run with a check prints them even on a line that resolved
  // nothing.
  const hasCrossfall = json.params.crossfall != null;
  const columns = hasCrossfall
    ? [...STATION_SECTIONS_CSV_COLUMNS, ...STATION_SECTIONS_CROSSFALL_CSV_COLUMNS]
    : STATION_SECTIONS_CSV_COLUMNS;
  const out = [...stationCsvHeaderLines(json, o, 'Station cross-sections'), columns.join(',')];
  for (const l of lines) {
    const row = [n(l.staDisplayM, 2), n(l.staInternalM, 2), n(l.cutAreaM2), n(l.fillAreaM2), n(l.coveragePct, 1)];
    if (hasCrossfall) row.push(...crossfallCells(l));
    out.push(row.join(','));
  }
  return `${out.join('\n')}\n`;
}

/**
 * The station lines a request covers. A selection of BINS `[lo, hi]` spans
 * station LINES `[lo, hi + 1]`: the last bin's end station is a section too,
 * and dropping it would silently shorten every export by one row.
 */
function sectionLinesInRange(json: StationVolumesJson, o: StationExportOptions) {
  const all = json.stations;
  if (!all || all.length === 0) return null;
  if (o.fromBin == null && o.toBin == null) return all;
  const last = all.length - 1;
  const lo = Math.max(0, Math.min(o.fromBin ?? 0, o.toBin ?? last));
  const hi = Math.min(last, Math.max(o.fromBin ?? 0, o.toBin ?? last) + 1);
  return lo <= hi ? all.slice(lo, hi + 1) : [];
}

/** `station_sections_1+000-1+500.csv`. */
export function stationSectionsFilename(json: StationVolumesJson, o: StationExportOptions = {}): string {
  const lines = sectionLinesInRange(json, o);
  if (!lines || lines.length === 0) return 'station_sections.csv';
  const from = staOnly(lines[0].staDisplayM, 0);
  const to = staOnly(lines[lines.length - 1].staDisplayM, 0);
  return `station_sections_${from}-${to}.csv`;
}

/**
 * `station_volumes_1+000-1+500.csv` — named after what is inside it, so two
 * downloads of different ranges never collide in the Downloads folder.
 * Station formatting comes from `format.ts` (ruling G10 / T12.3).
 */
export function stationExportFilename(json: StationVolumesJson, o: StationExportOptions = {}): string {
  const bins = binsInRange(json, o);
  if (bins.length === 0) return 'station_volumes.csv';
  const from = staOnly(bins[0].staStartDisplayM, 0);
  const to = staOnly(bins[bins.length - 1].staEndDisplayM, 0);
  return `station_volumes_${from}-${to}.csv`;
}
